import React, { useState, useEffect } from 'react';
import {
  Plug, Network, Activity, Workflow, Settings,
  ChevronLeft, ChevronRight, LayoutDashboard, ChevronDown, ChevronUp,
  FolderKanban, Users, LogOut, DollarSign, Briefcase,
  BrainCircuit, Bot, MessageSquare, ShieldCheck, Wrench, Globe, FlaskConical,
  Database, Shield, TrendingUp, BookOpen, Clock, Radio, BarChart2, Boxes,
  Sparkles, Compass, Target, Play, GraduationCap, Siren, LifeBuoy,
  FileText, Trash2, GitBranch, GitPullRequest, FolderOpen, MousePointerClick, KeyRound,
  DraftingCompass, Package,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTimezone, COMMON_TIMEZONES } from '../lib/timezone';
import { useAuth } from './TenantContext';
import { usePermission } from '../hooks/usePermission';
import { useAppStore } from '../store/appStore';
import { useAssistantStore, useTenantConversations } from '../store/assistantStore';
import { useAuthStore } from '../store/authStore';
import { useHumanActionsStore } from '../store/humanActionsStore';
import { useReleaseStore } from '../store/releaseStore';
import { usePermissionStore } from '../store/permissionStore';
import { useUiStore } from '../store/uiStore';
import { useTourStore } from '../tour/tourStore';
import { TOURS } from '../tour/steps';
import { openPurposesDialog } from '../modules/purposes/PurposesDialog';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇸🇻' },
];

// ── maic icon SVG ──────────────────────────────────────────────────────────

const MaicIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="18" y1="18" x2="9"  y2="9"  stroke="var(--color-brand)" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="26" y1="18" x2="35" y2="9"  stroke="var(--color-brand)" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="18" y1="26" x2="9"  y2="35" stroke="var(--color-brand)" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="26" y1="26" x2="35" y2="35" stroke="var(--color-brand)" strokeWidth="2.8" strokeLinecap="round" />
    <line x1="26" y1="22" x2="31" y2="22" stroke="var(--color-brand)" strokeWidth="2.8" strokeLinecap="round" />
    <circle cx="8"  cy="8"  r="5.5" fill="var(--color-brand)" />
    <circle cx="36" cy="8"  r="5.5" fill="var(--color-brand)" />
    <circle cx="8"  cy="36" r="5.5" fill="var(--color-brand)" />
    <circle cx="36" cy="36" r="5.5" fill="var(--color-brand)" />
    <circle cx="33" cy="22" r="3.5" fill="var(--color-brand)" />
    <rect x="17" y="17" width="10" height="10" rx="2.5" fill="var(--color-brand)" />
  </svg>
);

// ── Nav items ──────────────────────────────────────────────────────────────

export interface NavItem {
  id: string;
  label: string;
  i18nKey: string;
  icon: React.ReactNode;
  active: boolean;
  comingSoon?: boolean;
  path: string;
  adminOnly?: boolean;
  superadminOnly?: boolean;
  alwaysVisible?: boolean;
  /** Permission module that gates the item when it is not its path. */
  module?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'files', label: 'Archivos', i18nKey: 'nav.files', icon: <FolderOpen size={16} />, active: true, path: 'files', alwaysVisible: true },
  { id: 'apps',          label: 'Dashboards',   i18nKey: 'nav.dashboards',  icon: <LayoutDashboard size={16} />, active: true, path: 'apps' },
  { id: 'apps-app',      label: 'Apps',         i18nKey: 'nav.apps',        icon: <LayoutDashboard size={16} />, active: true, path: 'apps-app' },
  // Documents (QUIVER-11) are read like dashboards: gated by the same module.
  { id: 'documents',     label: 'Documentos',   i18nKey: 'nav.documents',   icon: <FileText size={16} />,        active: true, path: 'documents', module: 'apps' },
  { id: 'workbench',     label: 'Workbench',    i18nKey: 'nav.workbench',   icon: <BookOpen size={16} />,        active: true, path: 'workbench', alwaysVisible: true },
  { id: 'connectors',    label: 'Connectors',   i18nKey: 'nav.connectors',  icon: <Plug size={16} />,            active: true, path: 'connectors' },
  { id: 'ontology',      label: 'Ontology',     i18nKey: 'nav.ontology',    icon: <Network size={16} />,         active: true, path: 'ontology' },
  { id: 'data',          label: 'Data',         i18nKey: 'nav.data',        icon: <Database size={16} />,        active: true, path: 'data' },
  { id: 'pipelines',     label: 'Pipelines',    i18nKey: 'nav.pipelines',   icon: <Workflow size={16} />,        active: true, path: 'pipelines' },
  { id: 'logic',         label: 'Logic Studio', i18nKey: 'nav.logicStudio', icon: <BrainCircuit size={16} />,    active: true, path: 'logic' },
  { id: 'agents',        label: 'Agent Studio', i18nKey: 'nav.agentStudio', icon: <Bot size={16} />,             active: true, path: 'agents' },
  { id: 'external-apps', label: 'App Studio',   i18nKey: 'nav.appStudio',   icon: <Boxes size={16} />,           active: true, path: 'external-apps', adminOnly: true },
  { id: 'evals',         label: 'Evals',        i18nKey: 'nav.evals',       icon: <FlaskConical size={16} />,    active: true, path: 'evals' },
  { id: 'value',         label: 'Value Monitor',i18nKey: 'nav.value',       icon: <TrendingUp size={16} />,      active: true, path: 'value' },
  { id: 'scenarios',     label: 'Scenarios',    i18nKey: 'nav.scenarios',   icon: <BarChart2 size={16} />,       active: true, path: 'scenarios' },
  { id: 'activity',      label: 'Activity',     i18nKey: 'nav.activity',    icon: <Activity size={16} />,        active: true, path: 'activity' },
  { id: 'insights',      label: 'Insights',     i18nKey: 'nav.insights',    icon: <Sparkles size={16} />,        active: true, path: 'insights' },
  { id: 'predictions',   label: 'Predictions',  i18nKey: 'nav.predictions', icon: <Target size={16} />,          active: true, path: 'predictions' },
  { id: 'discovery',     label: 'Discovery',    i18nKey: 'nav.discovery',   icon: <Compass size={16} />,         active: true, path: 'discovery' },
  // Solution designs (SCOPING-1) are app rows read like dashboards.
  { id: 'solution-design', label: 'Solution design', i18nKey: 'nav.solutionDesign', icon: <DraftingCompass size={16} />, active: true, path: 'solution-design', module: 'apps' },
  { id: 'task-mining',   label: 'Task Mining',  i18nKey: 'nav.taskMining',  icon: <MousePointerClick size={16} />, active: true, path: 'task-mining', adminOnly: true },
  { id: 'operations',    label: 'Operations',   i18nKey: 'nav.operations',  icon: <Radio size={16} />,           active: true, path: 'operations', alwaysVisible: true },
  { id: 'utilities',     label: 'Utilities',    i18nKey: 'nav.utilities',   icon: <Wrench size={16} />,          active: true, path: 'utilities' },
  { id: 'human-actions', label: 'Actions',      i18nKey: 'nav.actions',     icon: <ShieldCheck size={16} />,     active: true, path: 'human-actions', alwaysVisible: true },
  { id: 'branches',      label: 'Branches',     i18nKey: 'nav.branches',    icon: <GitBranch size={16} />,       active: true, path: 'branches', alwaysVisible: true },
  { id: 'changes',       label: 'Changes',      i18nKey: 'nav.changes',     icon: <GitPullRequest size={16} />,  active: true, path: 'changes', alwaysVisible: true },
  { id: 'solutions',      label: 'Solutions',      i18nKey: 'nav.solutions',     icon: <Package size={16} />,  active: true, path: 'solutions', alwaysVisible: true },
  { id: 'admin',         label: 'Admin',        i18nKey: 'nav.admin',       icon: <Shield size={16} />,          active: true, path: 'admin', adminOnly: true },
  { id: 'platform',      label: 'Platform',     i18nKey: 'nav.platform',    icon: <Globe size={16} />,           active: true, path: 'platform', superadminOnly: true },
  { id: 'incidents',     label: 'Incidents',    i18nKey: 'nav.incidents',   icon: <Siren size={16} />,           active: true, path: 'incidents', superadminOnly: true },
  { id: 'settings',      label: 'Settings',     i18nKey: 'nav.settings',    icon: <Settings size={16} />,        active: true, path: 'settings', alwaysVisible: true },
];

const MAIC_SUBITEMS: NavItem[] = [
  { id: 'projects', label: 'Projects', i18nKey: 'nav.projects', icon: <Briefcase size={14} />, active: true, path: 'projects' },
  { id: 'finance',  label: 'Finance',  i18nKey: 'nav.finance',  icon: <DollarSign size={14} />, active: true, path: 'finance' },
];

// ── Component ──────────────────────────────────────────────────────────────

interface NavRailProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  additionalItems?: NavItem[];
}

export const NavRail: React.FC<NavRailProps> = ({ currentPage, onNavigate, additionalItems = [] }) => {
  const navCollapsed = useUiStore((s) => s.navCollapsed);
  const toggleNav = useUiStore((s) => s.toggleNav);
  const expanded = !navCollapsed;
  const [appsExpanded, setAppsExpanded] = useState(true);
  const [maicExpanded, setMaicExpanded] = useState(true);
  const [maicFlyout, setMaicFlyout] = useState<{ top: number } | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentUser, logout } = useAuth();
  const [tz, setTz] = useTimezone();
  const { canAdminister, isPlatformOperator, canAccess, modules, effective } = usePermission();
  const { t, i18n } = useTranslation();
  const { apps } = useAppStore();
  const { toggle: toggleAssistant, open: assistantOpen } = useAssistantStore();
  const conversations = useTenantConversations();
  const isImpersonating = !!useAuthStore(s => s.user?.impersonated_by);
  // Each guided demo storyboard references apps/agents seeded only in its
  // demo tenant — the launcher shows exactly where a TOURS entry exists.
  const tourTenantId = useAuthStore(s => s.user?.tenant_id);
  const hasGuidedDemo = !!(tourTenantId && TOURS[tourTenantId]);
  const { pendingCount, fetchPending } = useHumanActionsStore();
  const pendingChanges = useReleaseStore(s => s.pendingApprovals);
  const fetchChangeRequests = useReleaseStore(s => s.fetchChangeRequests);
  const userId = useAuthStore(s => s.user?.id);
  const effectiveLoaded = usePermissionStore(s => s.loaded);
  const fetchEffective = usePermissionStore(s => s.fetchEffective);

  // Poll for pending actions every 30s
  useEffect(() => {
    fetchPending();
    const interval = setInterval(fetchPending, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Poll change requests awaiting review every 60s (badge on "Changes").
  useEffect(() => {
    const poll = () => fetchChangeRequests('in_review');
    poll();
    const interval = setInterval(poll, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Resolve granular permissions for the signed-in user (drives nav gating).
  useEffect(() => {
    if (userId) fetchEffective();
  }, [userId, fetchEffective]);

  // Module access, not a role. The old `|| currentUser?.role === 'ADMIN'`
  // fallback to TenantContext is gone with it: two sources for one answer is
  // how the nav ended up disagreeing with the API.
  const isAdmin = canAdminister;
  const width = expanded ? 220 : 56;

  const canSee = (moduleId: string): boolean => {
    if (isAdmin) return true;
    // Use JWT modules if available, otherwise fall back to TenantContext allowed_modules
    if (modules.length > 0) return modules.includes(moduleId);
    const mods = currentUser?.allowed_modules;
    if (!mods || mods.length === 0) return true;
    return mods.includes(moduleId);
  };

  const navBtn = (
    item: NavItem,
    isActive: boolean,
    onClick: () => void,
    indented = false,
    small = false,
  ) => (
    <button
      key={item.id}
      data-tour={`nav-${item.id}`}
      onClick={onClick}
      title={!expanded ? item.label : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', height: small ? 34 : 38,
        padding: expanded ? (indented ? '0 16px 0 32px' : '0 14px') : '0',
        justifyContent: expanded ? 'flex-start' : 'center',
        backgroundColor: isActive ? 'var(--color-nav-active-bg)' : 'transparent',
        color: isActive ? 'var(--color-nav-text-active)' : item.comingSoon ? 'var(--color-nav-text-dim)' : 'var(--color-nav-text)',
        cursor: item.active ? 'pointer' : 'default',
        transition: 'background-color 80ms, color 80ms',
        borderLeft: isActive ? '2px solid var(--color-brand)' : '2px solid transparent',
        borderTop: 'none', borderRight: 'none', borderBottom: 'none',
        flexShrink: 0, textAlign: 'left',
      }}
      onMouseEnter={(e) => {
        if (!isActive && item.active) {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)';
          (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text-hover)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
          (e.currentTarget as HTMLElement).style.color = item.comingSoon ? 'var(--color-nav-text-dim)' : 'var(--color-nav-text)';
        }
      }}
    >
      <span style={{ flexShrink: 0, lineHeight: 0, opacity: item.comingSoon ? 0.4 : 1 }}>{item.icon}</span>
      {expanded && (
        <>
          <span style={{
            fontSize: small ? 12 : 13, fontWeight: isActive ? 500 : 400,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            opacity: item.comingSoon ? 0.4 : 1,
          }}>
            {t(item.i18nKey, item.label)}
          </span>
          {item.comingSoon && (
            <span style={{
              marginLeft: 'auto', fontSize: 9, color: 'var(--color-nav-text-dim)',
              border: '1px solid var(--color-nav-soon-border)', padding: '1px 4px',
              flexShrink: 0, letterSpacing: '0.04em',
            }}>
              {t('nav.soon')}
            </span>
          )}
        </>
      )}
    </button>
  );

  return (
    <nav style={{
      width, minWidth: width, height: '100vh',
      backgroundColor: isImpersonating ? 'var(--color-nav-impersonating-bg)' : 'var(--color-nav-bg)',
      display: 'flex', flexDirection: 'column',
      transition: 'width 120ms ease-out, min-width 120ms ease-out',
      overflow: 'hidden',
      borderRight: isImpersonating ? '2px solid #DC2626' : '1px solid var(--color-nav-border)',
      flexShrink: 0, position: 'relative', zIndex: 10,
    }}>
      {/* Logo */}
      <div style={{
        height: 52, display: 'flex', alignItems: 'center',
        padding: expanded ? '0 8px 0 14px' : '0', justifyContent: expanded ? 'flex-start' : 'center',
        borderBottom: '1px solid var(--color-nav-border)', gap: 10, flexShrink: 0,
      }}>
        {expanded ? (
          <>
            <MaicIcon size={22} />
            <span style={{ color: 'var(--color-nav-wordmark)', fontSize: 15, fontWeight: 700, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
              maic
            </span>
            <button
              onClick={toggleNav}
              title={t('nav.collapseSidebar', 'Collapse sidebar')}
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', background: 'transparent', color: 'var(--color-nav-text)', cursor: 'pointer', flexShrink: 0, transition: 'background-color 80ms, color 80ms' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-border)'; (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text-hover)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text)'; }}
            >
              <ChevronLeft size={18} />
            </button>
          </>
        ) : (
          <button
            onClick={toggleNav}
            title={t('nav.expandSidebar', 'Expand sidebar')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--color-nav-text-soft)', cursor: 'pointer', transition: 'background-color 80ms, color 80ms' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-border)'; (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-wordmark)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text-soft)'; }}
          >
            <ChevronRight size={18} />
          </button>
        )}
      </div>

      {/* Nav items */}
      <div style={{ flex: 1, padding: '6px 0', overflowY: 'auto', overflowX: 'hidden' }}>
        {[...additionalItems, ...NAV_ITEMS.filter(item => !additionalItems.some(extra => extra.id === item.id))]
          .filter((item) => {
            // 'apps' (Dashboards) and 'apps-app' (Apps) are separate gateable
            // modules; gate each nav item by its own module id (== its path).
            const moduleForPath = item.module || item.path;
            // Constrained by a profile (effective doc present and not full):
            // show ONLY the modules the profile grants. This overrides
            // alwaysVisible / superadminOnly / adminOnly so a demoted user
            // (even a superadmin) sees exactly what their profile allows.
            if (effective && !effective.full) {
              // The non-gateable exemption that used to be spelled out here
              // lives in `moduleEnabled` now, so every caller gets it — the nav
              // rail was only one of the places that gated on a permission
              // which cannot exist (see AppShell's redirect).
              return canAccess(moduleForPath) && item.active;
            }
            if (item.alwaysVisible) return item.active;
            // `platform` and `incidents`: cross-tenant scope AND the module
            // being granted, mirroring the backend, which demands both. Scope
            // alone would show the console to an operator whose profile does
            // not grant it; the module alone would show it to everyone while
            // Phase 2 is pending, since an unassigned user resolves to full.
            if (item.superadminOnly) {
              return isPlatformOperator && canAccess(moduleForPath) && item.active;
            }
            if (item.adminOnly && !canAccess(moduleForPath)) return false;
            if (effectiveLoaded) {
              // Granular permission doc is the source of truth once loaded.
              if (!canAccess(moduleForPath)) return false;
            } else if (modules.length > 0 && !canAccess(moduleForPath)) {
              return false;
            } else if (modules.length === 0 && !canSee(moduleForPath)) {
              // Legacy TenantContext module check when no JWT modules present
              return false;
            }
            return item.active;
          })
          .map((item) => {
            const btn = navBtn(item, currentPage === item.path, () => item.active && onNavigate(item.path));
            // Overlay badge: human-actions pending count / change requests in review
            const badge = item.id === 'human-actions' ? pendingCount
              : item.id === 'changes' ? pendingChanges : 0;
            if (badge > 0 && expanded) {
              return (
                <div key={item.id} style={{ position: 'relative' }}>
                  {btn}
                  <span style={{
                    position: 'absolute', top: 8, right: 10,
                    fontSize: 9, fontWeight: 700, minWidth: 16, height: 16,
                    backgroundColor: item.id === 'changes' ? '#2563EB' : '#D97706', color: '#FFF',
                    borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', pointerEvents: 'none',
                  }}>{badge}</span>
                </div>
              );
            }
            return btn;
          })}

        {/* ── maic group — only visible to @maic.ai users ─────────────────
              The `(!effective || effective.full)` condition that used to open
              this was meant as "and not when a profile constrains the user".
              Phase 2 made `full` false for everyone, so it evaluated to false
              always and the group vanished for every user, superadmins
              included. Projects and Finance are not permission modules (no
              catalog row), so a profile has nothing to say about them either
              way; the honest gate is the one that was always doing the work —
              the email domain. */}
        {currentUser?.email?.endsWith('@maic.ai') && (canSee('projects') || canSee('finance')) && (
          <>
            <button
              onClick={(e) => {
                if (expanded) { setMaicExpanded((v) => !v); }
                else { const r = (e.currentTarget as HTMLElement).getBoundingClientRect(); setMaicFlyout((f) => (f ? null : { top: r.top })); }
              }}
              title={!expanded ? 'maic' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', height: 38,
                padding: expanded ? '0 14px' : '0',
                justifyContent: expanded ? 'flex-start' : 'center',
                backgroundColor: (currentPage === 'projects' || currentPage === 'finance') ? 'var(--color-nav-active-bg)' : 'transparent',
                color: (currentPage === 'projects' || currentPage === 'finance') ? 'var(--color-nav-text-active)' : 'var(--color-nav-text)',
                cursor: 'pointer', transition: 'background-color 80ms, color 80ms',
                borderLeft: (currentPage === 'projects' || currentPage === 'finance') ? '2px solid var(--color-brand)' : '2px solid transparent',
                borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 'projects' && currentPage !== 'finance') {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 'projects' && currentPage !== 'finance') {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text)';
                }
              }}
            >
              <span style={{ flexShrink: 0, lineHeight: 0 }}><FolderKanban size={16} /></span>
              {expanded && (
                <>
                  <span style={{ fontSize: 13, fontWeight: (currentPage === 'projects' || currentPage === 'finance') ? 500 : 400 }}>maic</span>
                  <span style={{ marginLeft: 'auto', lineHeight: 0, flexShrink: 0, color: 'var(--color-nav-icon)' }}>
                    {maicExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  </span>
                </>
              )}
            </button>
            {expanded && maicExpanded && MAIC_SUBITEMS.filter(s => canSee(s.path)).map((sub) =>
              navBtn(sub, currentPage === sub.path, () => onNavigate(sub.path), true, true),
            )}
            {/* Collapsed: sub-items in a fixed flyout (escapes the nav's overflow:hidden) */}
            {!expanded && maicFlyout && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setMaicFlyout(null)} />
                <div style={{
                  position: 'fixed', top: maicFlyout.top, left: 60, minWidth: 184,
                  backgroundColor: 'var(--color-nav-menu-bg)', border: '1px solid var(--color-nav-menu-border)', borderRadius: 6,
                  overflow: 'hidden', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.4)', padding: 4,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-nav-icon)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 10px 4px' }}>maic</div>
                  {MAIC_SUBITEMS.filter(s => canSee(s.path)).map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => { onNavigate(sub.path); setMaicFlyout(null); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px 8px 7px',
                        border: 'none', borderLeft: currentPage === sub.path ? '3px solid var(--color-brand)' : '3px solid transparent',
                        borderRadius: 4, cursor: 'pointer', fontSize: 13, textAlign: 'left',
                        fontWeight: currentPage === sub.path ? 500 : 400,
                        backgroundColor: currentPage === sub.path ? 'var(--color-nav-sub-active-bg)' : 'transparent',
                        color: currentPage === sub.path ? 'var(--color-nav-sub-active-text)' : 'var(--color-nav-text-soft)',
                      }}
                      onMouseEnter={(e) => { if (currentPage !== sub.path) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)'; }}
                      onMouseLeave={(e) => { if (currentPage !== sub.path) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                    >
                      <span style={{ lineHeight: 0, flexShrink: 0 }}>{sub.icon}</span>
                      {t(sub.i18nKey)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </>
        )}

      </div>

      {/* Bottom: user + collapse */}
      <div style={{ borderTop: '1px solid var(--color-nav-border)', padding: '8px 0', flexShrink: 0, position: 'relative' }}>

        {/* User dropdown menu */}
        {userMenuOpen && currentUser && (
          <>
            {/* Backdrop */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 99 }}
              onClick={() => setUserMenuOpen(false)}
            />
            <div style={{
              ...(expanded
                ? { position: 'absolute' as const, bottom: '100%', left: 8, right: 8 }
                : { position: 'fixed' as const, bottom: 12, left: 60, width: 232 }),
              backgroundColor: 'var(--color-nav-menu-bg)', border: '1px solid var(--color-nav-menu-border)',
              borderRadius: 4, overflow: 'hidden', zIndex: 100,
              boxShadow: '0 -4px 16px rgba(0,0,0,0.4)',
            }}>
              {/* Language selector */}
              <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid var(--color-nav-menu-border)' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-nav-text-dim)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Globe size={11} /> {t('user.language')}
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      style={{
                        flex: 1, height: 28, fontSize: 11, fontWeight: 500,
                        borderRadius: 3, cursor: 'pointer',
                        border: `1px solid ${i18n.language === lang.code ? 'var(--color-brand)' : 'var(--color-nav-menu-border)'}`,
                        backgroundColor: i18n.language === lang.code ? 'var(--color-nav-ai-bg)' : 'transparent',
                        color: i18n.language === lang.code ? 'var(--color-brand-text, var(--color-nav-ai-text))' : 'var(--color-nav-text)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                        transition: 'all 80ms',
                      }}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Timezone selector — drives time-range filters and
                  timestamp display platform-wide. */}
              <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid var(--color-nav-menu-border)' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-nav-text-dim)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Clock size={11} /> {t('user.timezone', 'Timezone')}
                </div>
                <select
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  style={{
                    width: '100%', height: 28, fontSize: 11,
                    backgroundColor: 'var(--color-nav-menu-bg)', color: 'var(--color-nav-text-hover)',
                    border: '1px solid var(--color-nav-menu-border)', borderRadius: 3, padding: '0 6px',
                    outline: 'none', cursor: 'pointer',
                  }}
                >
                  {COMMON_TIMEZONES.map((t) => (
                    <option key={t.tz} value={t.tz} style={{ backgroundColor: 'var(--color-nav-menu-bg)' }}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Propósitos de acceso (INTRO-6): activar uno con justificación. */}
              <div style={{ borderBottom: '1px solid var(--color-nav-menu-border)', padding: '4px 0' }}>
                <button
                  type="button"
                  data-testid="open-purposes"
                  onClick={() => { setUserMenuOpen(false); openPurposesDialog(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    height: 32, padding: '0 12px', color: 'var(--color-nav-text)',
                    fontSize: 12, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <KeyRound size={13} />
                  {t('user.purposes', 'Propósitos de acceso')}
                </button>
              </div>

              {/* Public policies. Same documents the sign-in screen links to,
                  reachable from inside a session — GDPR art. 13 transparency
                  and Meta's platform-review requirement both expect the
                  privacy policy and the deletion route to stay one click away.
                  Plain anchors in a new tab: these routes live outside the
                  auth gate, and opening them must not discard in-app work. */}
              <div style={{ borderBottom: '1px solid var(--color-nav-menu-border)', padding: '4px 0' }}>
                {[
                  { href: '/privacy', icon: <ShieldCheck size={13} />, label: t('legal.privacy') },
                  { href: '/terms', icon: <FileText size={13} />, label: t('legal.terms') },
                  { href: '/data-deletion', icon: <Trash2 size={13} />, label: t('legal.deletion') },
                ].map(item => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setUserMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      height: 32, padding: '0 12px', color: 'var(--color-nav-text)',
                      fontSize: 12, textDecoration: 'none',
                      transition: 'color 80ms, background-color 80ms',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-ai-text)';
                      (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-ai-hover-bg)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text)';
                      (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Sign out */}
              <button
                onClick={() => { logout(); setUserMenuOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', height: 36, padding: '0 12px',
                  backgroundColor: 'transparent', color: 'var(--color-nav-text)',
                  cursor: 'pointer', border: 'none', textAlign: 'left',
                  fontSize: 12, transition: 'color 80ms, background-color 80ms',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-danger-text)';
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-danger-bg)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.color = 'var(--color-nav-text)';
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }}
              >
                <LogOut size={13} />
                {t('user.signOut')}
              </button>
            </div>
          </>
        )}

        {/* Guided demo launcher — FERCO demo tenant only */}
        {hasGuidedDemo && (
          <button
            onClick={() => useTourStore.getState().start()}
            title={!expanded ? t('nav.demo', 'Guided demo') : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: expanded ? '6px 14px' : '6px',
              justifyContent: expanded ? 'flex-start' : 'center',
              backgroundColor: 'transparent',
              border: 'none', cursor: 'pointer',
              borderLeft: '2px solid transparent',
              transition: 'background-color 80ms',
              marginBottom: 2,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
          >
            <Play size={16} style={{ flexShrink: 0, color: 'var(--color-nav-icon)' }} />
            {expanded && (
              <span style={{ fontSize: 13, color: 'var(--color-nav-text)' }}>{t('nav.demo', 'Guided demo')}</span>
            )}
          </button>
        )}

        {/* Report an issue — any user; opens the global report drawer */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('nexus:report-issue'))}
          title={!expanded ? t('nav.reportIssue', 'Report an issue') : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: expanded ? '6px 14px' : '6px',
            justifyContent: expanded ? 'flex-start' : 'center',
            backgroundColor: 'transparent',
            border: 'none', cursor: 'pointer',
            borderLeft: '2px solid transparent',
            transition: 'background-color 80ms',
            marginBottom: 2,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
        >
          <LifeBuoy size={16} style={{ flexShrink: 0, color: 'var(--color-nav-icon)' }} />
          {expanded && (
            <span style={{ fontSize: 13, color: 'var(--color-nav-text)' }}>{t('nav.reportIssue', 'Report an issue')}</span>
          )}
        </button>

        {/* PAI Academy — the course home lives at the 'academy' page */}
        <button
          onClick={() => onNavigate('academy')}
          title={!expanded ? t('nav.academy', 'PAI Academy') : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: expanded ? '6px 14px' : '6px',
            justifyContent: expanded ? 'flex-start' : 'center',
            backgroundColor: 'transparent',
            border: 'none', cursor: 'pointer',
            borderLeft: '2px solid transparent',
            transition: 'background-color 80ms',
            marginBottom: 2,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
        >
          <GraduationCap size={16} style={{ flexShrink: 0, color: 'var(--color-nav-icon)' }} />
          {expanded && (
            <span style={{ fontSize: 13, color: 'var(--color-nav-text)' }}>{t('nav.academy', 'PAI Academy')}</span>
          )}
        </button>

        {/* Nexus Assistant button */}
        <button
          onClick={toggleAssistant}
          title={!expanded ? t('nav.assistant', 'Nexus Assistant') : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: expanded ? '6px 14px' : '6px',
            justifyContent: expanded ? 'flex-start' : 'center',
            backgroundColor: assistantOpen ? 'var(--color-nav-ai-bg)' : 'transparent',
            border: 'none', cursor: 'pointer',
            borderLeft: assistantOpen ? '2px solid var(--color-brand)' : '2px solid transparent',
            transition: 'background-color 80ms',
            marginBottom: 2,
          }}
          onMouseEnter={(e) => { if (!assistantOpen) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)'; }}
          onMouseLeave={(e) => { if (!assistantOpen) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
        >
          <MessageSquare size={16} style={{ flexShrink: 0, color: assistantOpen ? 'var(--color-brand)' : 'var(--color-nav-icon)' }} />
          {expanded && (
            <>
              <span style={{ fontSize: 13, color: assistantOpen ? 'var(--color-brand)' : 'var(--color-nav-text)', fontWeight: assistantOpen ? 500 : 400 }}>
                {t('nav.assistant', 'Assistant')}
              </span>
              {conversations.length > 0 && (
                <span style={{
                  marginLeft: 'auto', fontSize: 9, color: 'var(--color-brand)',
                  backgroundColor: 'var(--color-brand-dim)', padding: '1px 6px', borderRadius: 10, flexShrink: 0,
                  border: '1px solid var(--color-brand-border)',
                }}>
                  {conversations.length}
                </span>
              )}
            </>
          )}
        </button>

        {/* User row — clickable, opens dropdown */}
        {currentUser && (
          <button
            onClick={() => setUserMenuOpen((v) => !v)}
            title={!expanded ? currentUser.name : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: expanded ? '6px 14px' : '6px',
              justifyContent: expanded ? 'flex-start' : 'center',
              backgroundColor: userMenuOpen ? 'var(--color-nav-hover-bg)' : 'transparent',
              border: 'none', cursor: 'pointer',
              transition: 'background-color 80ms',
            }}
            onMouseEnter={(e) => {
              if (!userMenuOpen) (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-nav-hover-bg)';
            }}
            onMouseLeave={(e) => {
              if (!userMenuOpen) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            }}
          >
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              backgroundColor: 'var(--color-brand-dim)', border: '1px solid var(--color-brand-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: 'var(--color-nav-ai-text)', flexShrink: 0,
            }}>
              {currentUser.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            {expanded && (
              <>
                <div style={{ overflow: 'hidden', flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-nav-text-active)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentUser.name}
                  </div>
                  {isImpersonating ? (
                    <div style={{
                      fontSize: 9, color: '#fff', backgroundColor: '#DC2626',
                      padding: '1px 6px', borderRadius: 3, fontWeight: 700,
                      letterSpacing: '0.05em', display: 'inline-block', marginTop: 1,
                    }}>
                      {t('user.impersonating', 'IMPERSONATING')}
                    </div>
                  ) : (
                    <div style={{ fontSize: 10, color: 'var(--color-nav-text-dim)', letterSpacing: '0.04em' }}>
                      {currentUser.role}
                    </div>
                  )}
                </div>
                <ChevronDown size={11} style={{ color: 'var(--color-nav-text-dim)', flexShrink: 0, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
              </>
            )}
          </button>
        )}

      </div>

    </nav>
  );
};

export default NavRail;
